import { useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { Button2 } from "@/ui2/atoms/Button";
import type { CurrentAcuityScoreResponse } from "@/core/types";
import EllipsisVerticalIcon from "@/assets/images/icons2/icon--ellipsis-vertical.svg?component";
import { Menu2 } from "@/ui2/molecules/Menu";
import type { PermissionKey } from "@/features/access-control/types";
import { selectProfileEmail } from "@/redux/selectors/authSelectors";
import { Tooltip2 } from "@/ui2/molecules/Tooltip";
import { useFilterPermittedItems } from "@/features/access-control/hooks/useFilterPermittedItems";

import { AcuityHistoryModal } from "./AcuityHistoryModal";
import { ReportAcuityModal } from "./ReportAcuityModal";

type MenuItemId = "report-acuity" | "history";

type MenuItem = {
  id: MenuItemId;
  isDisabled?: boolean;
  label: string;
  permissionKey: PermissionKey;
};

type ReportAcuityMenuProps = {
  currentAcuityScore: CurrentAcuityScoreResponse;
};

export function ReportAcuityMenu({ currentAcuityScore }: ReportAcuityMenuProps) {
  const { approval_requests, effective_score } = currentAcuityScore;
  const [isReportAcuityModalOpen, setIsReportAcuityModalOpen] = useState(false);
  const [isAcuityHistoryModalOpen, setIsAcuityHistoryModalOpen] = useState(false);
  const email = useSelector(selectProfileEmail);

  const hasPendingApprovalRequest = approval_requests?.length > 0;
  const hasEffectiveScore = effective_score !== null;

  const MENU_ITEMS: MenuItem[] = useMemo(
    () => [
      {
        id: "report-acuity",
        isDisabled: hasPendingApprovalRequest,
        label: "Report Acuity",
        permissionKey: "acuityScoreReport",
      },
      {
        id: "history",
        label: "History",
        permissionKey: "acuityScoreHistory",
      },
    ],
    [hasPendingApprovalRequest],
  );

  const permittedItems = useFilterPermittedItems({
    items: [...MENU_ITEMS],
    predicateContext: { email },
  });

  if (!permittedItems.length) return null;

  const handleMenuItemClick = (key: MenuItemId) => {
    switch (key) {
      case "report-acuity":
        setIsReportAcuityModalOpen(true);
        break;

      case "history":
        setIsAcuityHistoryModalOpen(true);
        break;

      default:
        key satisfies never;
    }
  };

  if (!hasEffectiveScore)
    return (
      <Tooltip2 asChild text="Patient did not complete the A5 assessment">
        <Button2
          aria-label="Open menu"
          className="tw:cursor-not-allowed! tw:opacity-50"
          rawChildren
          variant="blank"
        >
          <EllipsisVerticalIcon className="tw:text-fill-teal-primary" />
        </Button2>
      </Tooltip2>
    );

  return (
    <>
      <Menu2 items={permittedItems} onAction={handleMenuItemClick} placement="bottom right" />

      <ReportAcuityModal
        isModalOpen={isReportAcuityModalOpen}
        setIsModalOpen={setIsReportAcuityModalOpen}
      />

      <AcuityHistoryModal
        isModalOpen={isAcuityHistoryModalOpen}
        setIsModalOpen={setIsAcuityHistoryModalOpen}
      />
    </>
  );
}
